import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';

import type { MutationResponse } from '@/types/api/mutations';
import type { TFunction } from 'i18next';
import type {
  DefaultValues,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import type { ZodType } from 'zod';

import { type FormFields, typedFields } from '@/components/Form';

import {
  processErrorResponse,
  processSuccessResponse,
} from '@/lib/processApiResponse';

import { DisplayToast } from '@/helpers';

import { HttpClient } from '@/network/HttpClient';

// --- Types ---

type UseAppFormConfig<
  TValues extends FieldValues,
  TOutput extends FieldValues,
  TId extends number | string,
> = {
  schema: (t: TFunction, isEditMode: boolean) => ZodType<TOutput, TValues>;

  /** Form default values (inline, entity-dependent) */
  defaultValues: DefaultValues<NoInfer<TValues>>;

  /** Data transformation before submit — returns FormData (file uploads) or plain object */
  transform: (
    values: NoInfer<TOutput>,
    id?: TId,
  ) => FormData | Record<string, unknown>;

  /** Entity ID — determines mode (create vs edit) and submit URL. Defaults to `number`; pass `TId = string` for UUID PKs */
  entityId?: TId;

  /** API endpoint (base URL, hook appends `/{id}` for edit) */
  api_url: string;

  /** Redirect URL after successful submit */
  redirectUrl: string;

  /** Extract entity ID from response and redirect to `{redirectUrl}/{id}` */
  entityIdKey?: string;

  /** Map server error field names to form field names (e.g. `{ locations: 'location_ids' }`) */
  errorFieldMap?: Partial<Record<string, keyof NoInfer<TValues> & string>>;

  /** Custom success handler — when provided, replaces default processSuccessResponse + redirect */
  onSuccess?: (response: MutationResponse) => void;

  /** Toast the response `flash_message` before a custom `onSuccess` */
  shouldToastFlashMessage?: boolean;

  onError?: (error: unknown) => void;
};

type UseAppFormReturn<
  TValues extends FieldValues,
  TOutput extends FieldValues,
> = {
  /** react-hook-form instance — pass to `<Form form={form}>` */
  form: UseFormReturn<TValues, unknown, TOutput>;
  formFields: FormFields<TValues>;
  /** Submit handler — pass to `<FormWrapper onSubmit={onSubmit}>` */
  onSubmit: () => void;
  /** Mutation loading state — pass to `<FormWrapper isPending={isPending}>` */
  isPending: boolean;
  /** `'create'` or `'edit'` — pass to `<FormWrapper mode={mode}>` */
  mode: 'create' | 'edit';
  /** Shorthand for `mode === 'edit'` */
  isEditMode: boolean;
  /** Translation function — use for field labels and titles */
  t: TFunction;
};

// --- Mutation types ---

type FormMutationParams = {
  url: string;
  method: 'POST' | 'PUT';
  data: FormData | Record<string, unknown>;
};

// --- Hook ---

export const useAppForm = <
  TValues extends FieldValues,
  TOutput extends FieldValues,
  TId extends number | string = number,
>(
  config: UseAppFormConfig<TValues, TOutput, TId>,
): UseAppFormReturn<TValues, TOutput> => {
  const {
    schema: schemaFactory,
    defaultValues,
    transform,
    entityId,
    api_url,
    redirectUrl,
    entityIdKey,
    errorFieldMap,
    onSuccess: customOnSuccess,
    shouldToastFlashMessage,
    onError: customOnError,
  } = config;

  const { t } = useTranslation();

  const mode = entityId ? 'edit' : 'create';

  const schema = useMemo(
    () => schemaFactory(t, mode === 'edit'),
    [schemaFactory, t, mode],
  );

  const form = useForm<TValues, unknown, TOutput>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (params: FormMutationParams) =>
      HttpClient.request<MutationResponse>({
        method: params.method,
        url: params.url,
        data: params.data,
      }),
  });

  const onSubmit = useCallback(() => {
    form.handleSubmit((values) => {
      const submitUrl = entityId ? `${api_url}/${entityId}` : api_url;
      const method = entityId ? 'PUT' : 'POST';
      const data = transform(values, entityId);

      mutate(
        { url: submitUrl, method, data },
        {
          onSuccess: (response) => {
            if (!response) return;
            if (shouldToastFlashMessage && response.flash_message) {
              DisplayToast.success(response.flash_message);
            }
            if (customOnSuccess) {
              customOnSuccess(response);
            } else {
              processSuccessResponse({
                response,
                manualRedirectUrl: redirectUrl,
                entityIdKey,
              });
            }
          },
          onError: (error) => {
            processErrorResponse({
              error,
              form,
              errorFieldMap,
            });
            customOnError?.(error);
          },
        },
      );
    })();
  }, [
    entityId,
    api_url,
    transform,
    redirectUrl,
    entityIdKey,
    errorFieldMap,
    customOnSuccess,
    shouldToastFlashMessage,
    customOnError,
    form,
    mutate,
  ]);

  const isEditMode = mode === 'edit';

  return {
    form,
    formFields: typedFields<TValues>(),
    onSubmit,
    isPending,
    mode,
    isEditMode,
    t,
  };
};
